import { CONNECTOR_LIMITS, CONNECTOR_VARIABLE_PREFIX } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, Icon, Input } from "@sagot/ui";
import { useId } from "react";
import { CONNECTOR_EXAMPLES, factOf, mappedVariables, mappingProblems, variableNameOf, variableTarget } from "./connector-model";
import type { HttpRequestMapping, HttpRequestStep } from "../types";
import type { FormProps } from "./webhook-form";

const C = "engage.workflows.connector";
const NOT_VARIABLE_CHARS = /[^a-z0-9_]/g;

/** Response fields to save: a JSON Pointer into the answer and the run variable that receives it. */
export function ConnectorMapping({ step, disabled, onChange }: FormProps<HttpRequestStep>) {
  const { t } = useTranslation();
  const id = useId();
  const map = step.map ?? [];
  const problems = mappingProblems(map);
  const set = (next: HttpRequestMapping[]) => { onChange({ ...step, map: next }); };
  const update = (index: number, patch: Partial<HttpRequestMapping>) => { set(map.map((row, at) => (at === index ? { ...row, ...patch } : row))); };
  return (
    <fieldset className="space-y-2" data-testid="connector-map">
      <legend className="label">{t(`${C}.map.label`)}</legend>
      <p className="hint -mt-1">{t(`${C}.map.hint`, { example: CONNECTOR_EXAMPLES.mapped })}</p>
      {map.length === 0 ? <p className="text-xs text-fg-muted">{t(`${C}.map.empty`, { ok: factOf(step, "ok"), status: factOf(step, "status") })}</p> : null}
      {map.map((row, index) => {
        const problem = problems[index];
        const errorId = `${id}-${String(index)}-error`;
        const label = t(`${C}.map.row`, { n: index + 1 });
        return (
          <div key={index} className="space-y-1">
            <div className="flex items-start gap-2">
              <Input aria-label={`${label}: ${t(`${C}.map.pointer`)}`} aria-describedby={problem ? errorId : undefined} invalid={problem === "pointer"} className="min-w-0 flex-1 font-mono text-xs" placeholder={t(`${C}.map.pointerPlaceholder`)} disabled={disabled} maxLength={CONNECTOR_LIMITS.pointer} value={row.from} onChange={(event) => { update(index, { from: event.target.value }); }} />
              <span aria-hidden="true" className="pt-2.5 text-fg-subtle"><Icon name="arrow-right" className="size-4" /></span>
              <div className="flex min-w-0 flex-1 items-center rounded-md border border-line focus-within:ring-2 focus-within:ring-primary">
                <span aria-hidden="true" className="pl-2 font-mono text-xs text-fg-muted">{CONNECTOR_VARIABLE_PREFIX}</span>
                <Input aria-label={`${label}: ${t(`${C}.map.variable`)}`} aria-describedby={problem ? errorId : undefined} invalid={problem === "variable" || problem === "duplicate"} className="min-w-0 flex-1 border-0 font-mono text-xs shadow-none focus:ring-0" placeholder={t(`${C}.map.variablePlaceholder`)} disabled={disabled} maxLength={CONNECTOR_LIMITS.variableName} value={variableNameOf(row.to)} onChange={(event) => { update(index, { to: variableTarget(event.target.value.toLowerCase().replace(NOT_VARIABLE_CHARS, "")) }); }} />
              </div>
              <Button type="button" variant="ghost" size="sm" iconOnly disabled={disabled} aria-label={`${t(`${C}.map.remove`)}: ${label}`} onClick={() => { set(map.filter((_, at) => at !== index)); }}>
                <Icon name="trash" />
              </Button>
            </div>
            {problem ? <p id={errorId} role="alert" className="text-xs font-semibold text-danger">{t(`${C}.problems.map.${problem}`)}</p> : null}
          </div>
        );
      })}
      {map.length < CONNECTOR_LIMITS.mapRows ? (
        <Button type="button" variant="secondary" size="sm" disabled={disabled} onClick={() => { set([...map, { from: "", to: CONNECTOR_VARIABLE_PREFIX }]); }}>
          <Icon name="plus" />
          {t(`${C}.map.add`)}
        </Button>
      ) : null}
      <p className="text-xs text-fg-subtle" data-testid="connector-facts">
        {t(`${C}.map.facts`)}:{" "}
        {[factOf(step, "ok"), factOf(step, "status"), ...mappedVariables(step).map((name) => `{{${name}}}`)].map((fact, position) => (
          <span key={fact}>
            {position > 0 ? ", " : ""}
            <code className="font-mono">{fact}</code>
          </span>
        ))}
      </p>
    </fieldset>
  );
}
